<script setup>
import foundWays from "@/config/lists/foundWays";
import jobPositionStatuses from "@/config/lists/jobPositionStatuses";
import contractTypes from "@/config/lists/contractTypes";
import workingHours from "@/config/lists/workingHours";
import workingShiftsOriginal from "@/config/lists/workingShifts";
import workWeekSchedules from "@/config/lists/workWeekSchedules";
import salaryRange from "@/config/lists/salaryRange";
import previousExperience from "@/config/lists/previousExperience";
import gendersJobPosition from "@/config/lists/gendersJobPosition";
import agePreferences from "@/config/lists/agePreferences";
import drivingLicenses from "@/config/lists/drivingLicenses";
import { useJobPositionStore, useListStore } from "@/stores";
import { computed, onMounted } from "vue";

const adminLocations = computed(() => listStore.adminLocations);
const adminLocationCities = computed(() => listStore.adminLocationCities);

const workingShifts = workingShiftsOriginal.filter(
  (shift) => shift.value !== null
);

async function onAdminLocationSelected(value) {
  advanced.value.location = null;
  await listStore.fetchAdminLocationCities(value.id);
}

const jobPositionStore = useJobPositionStore();
const listStore = useListStore();

const keyword = computed({
  get: () => jobPositionStore.filters.keyword,
  set: (value) => (jobPositionStore.filters.keyword = value),
});

const advanced = computed({
  get: () => jobPositionStore.filters.advanced,
  set: (value) => (jobPositionStore.filters.advanced = value),
});

const appliedFilters = computed(() => {
  const adv = advanced.value;
  return Object.values(adv).filter((val) => {
    if (val == null) return false; // null or undefined
    if (Array.isArray(val)) return val.length > 0; // empty array
    if (typeof val === "string") return val.trim() !== ""; // empty string
    return true; // all other non-null, non-empty values
  }).length;
});

const companies = computed(() => listStore.companies);

const handleResetBtnClick = () => {
  jobPositionStore.resetAction();
};

const hideSimpleSearch = computed(() => {
  return false;
});

const variant = "company";

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

const communicationResponsibles = computed(
  () => listStore.communicationResponsibles
);
const branches = computed(() => listStore.branches);


onMounted(async () => {
  await listStore.fetchCompanies();
  await listStore.fetchAdminLocations();
  await listStore.fetchBranches();
  await listStore.fetchCommunicationResponsibles();
});
</script>

<template>
  <div v-if="!hideSimpleSearch">
    <v-text-field v-model="keyword" name="searchbar" label="Search (by name)" />
  </div>
  <v-expansion-panels class="mb-5">
    <v-expansion-panel :class="`advanced-filters-panel panel-${variant}`">
      <v-expansion-panel-title :class="`title-${variant}`">
        <v-icon icon="mdi-filter" />
        <b>Advanced Filters</b>
        <div class="ml-2 text-caption font-weight-bold">
          ({{ appliedFilters }}
          <span v-if="appliedFilters == 1">filter</span>
          <span v-else>filters</span> applied)
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div class="d-flex ga-5">
          <div class="flex-1-0-0">
            <v-text-field v-model="advanced.name" name="name" label="Name" />
          </div>

          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.date"
              label="Date"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <Autocomplete
              v-model="advanced.company"
              label="Company"
              :items="companies"
              :multiple="true"
              :chips="true"
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.foundWay"
              label="How did we find the job position?"
              :items="foundWays"
              item-title="name"
              item-value="value"
              variant="outlined"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.status"
              label="Status"
              :items="jobPositionStatuses"
              item-title="name"
              item-value="value"
              variant="outlined"
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.parentLocation"
              label="Area"
              :items="adminLocations"
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
              @update:model-value="onAdminLocationSelected"
            />
          </div>

          <div class="flex-1-0-0" v-if="advanced.parentLocation">
            <v-select
              v-if="advanced.parentLocation"
              v-model="advanced.location"
              label="Municipality/Suburb"
              :items="adminLocationCities"
              item-title="name"
              item-value="value"
              return-object
              variant="outlined"
              hide-details="auto"
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.contractType"
              :items="contractTypes"
              clearable
              item-title="name"
              item-value="value"
              variant="outlined"
            >
              <template #label> What is the type of contract? </template>
            </v-select>
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.workingHours"
              label="Working Hours"
              :items="workingHours"
              item-title="name"
              item-value="value"
              variant="outlined"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.workingShifts"
              label="Working Shifts"
              :items="workingShifts"
              variant="outlined"
              item-title="name"
              item-value="value"
              clearable
              multiple
            />
          </div>
        </div>
        <div class="d-flex ga-5">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.workWeekSchedule"
              :items="workWeekSchedules"
              clearable
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
            >
              <template #label> Workweek schedule </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.salaryRange"
              :items="salaryRange"
              clearable
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
            >
              <template #label> Salary range </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.previousExperience"
              :items="previousExperience"
              clearable
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
            >
              <template #label> Previous Experience </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.gender"
              :items="gendersJobPosition"
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
            >
              <template #label> Gender preference </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.age"
              :items="agePreferences"
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Age preference </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.drivingLicense"
              :items="drivingLicenses"
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label>
                What type of driver's license does it require?
              </template>
            </v-select>
          </div>
        </div>

        <div class="d-flex ga-5 mt-5 mb-5">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.offersTraining"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Offers training? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.contactWhatsapp"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Contact through Whatsapp? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.openToPwD"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Open to People with Disabilities? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.offersTransportation"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Transportation to/from workplace? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <Autocomplete
              v-model="advanced.responsible"
              label="Responsible"
              :items="communicationResponsibles"
              :multiple="false"
              :chips="false"
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.branch"
              label="Odyssea Branch"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              clearable
            />
          </div>
        </div>

        <div class="d-flex ga-5">
          <v-btn
            :color="variant"
            variant="outlined"
            @click="handleResetBtnClick"
          >
            Reset
          </v-btn>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
